import { makeAutoObservable } from 'mobx'

import { type ClientEvent, ClientEventType, ServerEventType } from '../sw/const-sw'

type PrecacheEntry = {
  url: string
  revision: string | null
  cached?: boolean
}

type ProgressSnapshot = {
  completed: number
  total: number
  timestamp: number
}

const PROGRESS_STORAGE_KEY = 'pwa-cache-progress'
const CACHED_ASSET_STORAGE_KEY = 'pwa-cached-assets'

type CachedAssetSnapshot = {
  entries: Record<string, boolean>
  total: number
  timestamp: number
}


const readProgressSnapshot = (): ProgressSnapshot | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }

  try {
    const raw = window.localStorage.getItem(PROGRESS_STORAGE_KEY)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as ProgressSnapshot
    if (typeof parsed.completed !== 'number' || typeof parsed.total !== 'number') {
      return null
    }

    return parsed
  } catch (error) {
    if (import.meta.env.VITE_NODE_ENV !== 'production') {
      console.warn('[PWA] Failed to read cached progress', error)
    }
    return null
  }
}

const writeProgressSnapshot = (snapshot: ProgressSnapshot) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }

  try {
    window.localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(snapshot))
  } catch (error) {
    if (import.meta.env.VITE_NODE_ENV !== 'production') {
      console.warn('[PWA] Failed to persist cache progress', error)
    }
  }
}

const readCachedAssetSnapshot = (): CachedAssetSnapshot | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }

  try {
    const raw = window.localStorage.getItem(CACHED_ASSET_STORAGE_KEY)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as CachedAssetSnapshot
    if (!parsed.entries) {
      return null
    }

    return parsed
  } catch (error) {
    if (import.meta.env.VITE_NODE_ENV !== 'production') {
      console.warn('[PWA] Failed to read cached asset snapshot', error)
    }
    return null
  }
}

const writeCachedAssetSnapshot = (snapshot: CachedAssetSnapshot) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }

  try {
    window.localStorage.setItem(CACHED_ASSET_STORAGE_KEY, JSON.stringify(snapshot))
  } catch (error) {
    if (import.meta.env.VITE_NODE_ENV !== 'production') {
      console.warn('[PWA] Failed to persist cached asset snapshot', error)
    }
  }
}

const toCacheKey = (entry: PrecacheEntry) => {
  // parse URL (handle relative paths lacking leading slash)
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const url = new URL(entry.url, origin)

  // encode each search params
  const params: string[] = []
  if (url.search) {
    for (const [key, param] of url.searchParams.entries()) {
      params.push(`${key}=${encodeURIComponent(param)}`)
    }
  }

  // append revision
  if (entry.revision) {
    params.push(`__WB_REVISION__=${encodeURIComponent(entry.revision)}`)
  }

  // use pathname, hostname is discarded
  return (entry.url.startsWith('http') ? url.origin : '') + // origin is not needed for same-origin request
    url.pathname +
    (params.length ? '?' : '') // append ? if params is not empty
    + params.join('&') // URI encoded parameters
}

class PwaState {
  private deferredPrompt: any = undefined
  installing = false

  hasUpdate = false
  registration?: ServiceWorkerRegistration = undefined // used for FCM push notification
  private waitingProc?: (reloadPage?: boolean) => Promise<void> = undefined

  // finer caching process status
  total = 0
  completed = 0
  errored = 0
  assetsMap: { [key: string]: PrecacheEntry } = {}
  private cachedAssets: Record<string, boolean> = {}
  private latestManifest: PrecacheEntry[] = []
  verifying = false
  assetsLoaded = false
  // to cancel cache verification when "install" event is received
  cancelVerify = false
  setCancelVerify(cancel: boolean) {
    this.cancelVerify = cancel
  }

  get loadPercent() {
    return Math.round(Math.min(this.completed, this.total) / (this.total || 1) * 100)
  }

  private channel = new BroadcastChannel('sw-messages')

  constructor() {
    this.restoreProgressSnapshot()
    this.restoreCachedAssets()
    this.completed = this.countCachedEntries()

    this.channel.addEventListener('message', async (event: MessageEvent<ClientEvent>) => {
      let needLogging = false
      switch (event.data.type) {
        case ClientEventType.install:
          this.setCancelVerify(true)
          this.updateProgress(0, event.data.data.length)
          this.resetCachedAssets()
          needLogging = true
          break
        case ClientEventType.handlerDidComplete: {
          this.trackCachedAsset(event.data.data.url, event.data.data.total)
          break
        }
        case ClientEventType.handlerDidError:
          this.updateError(event.data.data.count)
          needLogging = true
          break
        case ClientEventType.assets: {
          this.storeAssetsMetadata(event.data.data)
          needLogging = true
          break
        }
      }

      if (needLogging && import.meta.env.VITE_NODE_ENV !== 'production') {
        console.log('[PWA] Received', event.data)
      }
    })

    // request assets
    this.channel.postMessage({ type: ServerEventType.getAssets })

    makeAutoObservable(this, {
      assetsMap: false,
    })
  }

  sendLanguageCode(code: string) {
    this.channel.postMessage({
      type: ServerEventType.setLanguage,
      data: code,
    })
  }

  async verifyCacheAndSave() {
    if (!this.latestManifest.length || this.verifying) {
      return
    }

    this.verifying = true

    try {
      const manifestSnapshot = this.latestManifest.map((entry) => ({ ...entry }))
      this.setAssetsMap(manifestSnapshot)

      const cache = await caches.open(`workbox-precache-v2-${window.location.origin}/`)

      let cached = 0
      for (let i = 0; i < manifestSnapshot.length; i++) {
        const entry: PrecacheEntry = manifestSnapshot[i]

        if (this.cancelVerify) {
          break
        }

        // check cache status
        const res = await cache.match(toCacheKey(entry))
        const key = this.normalizeAssetUrl(entry.url)
        if (res?.ok && this.assetsMap[key]) {
          cached++
          this.assetsMap[key].cached = true
          this.cachedAssets[key] = true
        }
      }

      if (!this.cancelVerify) {
        this.updateProgress(cached, manifestSnapshot.length)
      }
      this.persistCachedAssets(manifestSnapshot.length)
    } finally {
      this.setCancelVerify(false)
      this.verifying = false
    }
  }

  private storeAssetsMetadata(assets: PrecacheEntry[]) {
    this.latestManifest = assets.map((entry) => ({ ...entry }))
    this.setAssetsMap(this.latestManifest)
    this.assetsLoaded = true
    // ensure future verifications (if re-enabled) are not blocked by stale flags
    this.setCancelVerify(false)
  }

  private setAssetsMap(assets: PrecacheEntry[]) {
    this.assetsMap = {}
    let cachedCount = 0
    const validKeys = new Set<string>()
    for (let i = 0; i < assets.length; i++) {
      const entry: PrecacheEntry = assets[i]
      const key = this.normalizeAssetUrl(entry.url)
      const isCached = !!this.cachedAssets[key]
      this.assetsMap[key] = entry
      this.cachedAssets[key] = isCached
      validKeys.add(key)
      entry.cached = isCached
      if (isCached) {
        cachedCount++
      }
    }
    this.pruneCachedAssets(validKeys)
    this.updateProgress(cachedCount, assets.length)
    this.persistCachedAssets(assets.length)
  }

  updateProgress(completed: number, total: number) {
    this.completed = completed
    this.total = total
    this.persistProgressSnapshot()
  }

  updateCompleted(completed: number) {
    this.completed = completed
    this.persistProgressSnapshot()
  }

  updateError(count: number) {
    this.errored = count
  }

  get canInstall() {
    return !!this.deferredPrompt
  }

  set promt(prompt: any) {
    this.deferredPrompt = prompt
  }

  onUpdate = (updateSW?: (reloadPage?: boolean) => Promise<void>) => {
    this.waitingProc = updateSW
    this.hasUpdate = !!this.waitingProc
    if (this.hasUpdate) {
      for (const key of Object.keys(this.assetsMap)) {
        this.cachedAssets[key] = true
      }
      this.updateProgress(this.total, this.total)
      this.persistCachedAssets(this.total)
    }
  }

  setRegistration = (registration?: ServiceWorkerRegistration) => {
    this.registration = registration
  }

  update = async () => {
    if (this.hasUpdate && this.waitingProc) {
      await this.waitingProc(true)
    }
    return this.hasUpdate
  }

  registerForAppInstall = () => {
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault()
      // Stash the event so it can be triggered later.
      this.promt = e
    })
  }

  promtInstall = async () => {
    if (this.deferredPrompt) {
      this.installing = true
      // Show the install prompt
      this.deferredPrompt.prompt()
      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice

      // We've used the prompt, and can't use it again, throw it away
      this.promt = null
      this.installing = false

      return outcome
    }
  }

  private restoreProgressSnapshot() {
    const snapshot = readProgressSnapshot()
    if (snapshot) {
      this.completed = snapshot.completed
      this.total = snapshot.total
    }
  }

  private persistProgressSnapshot() {
    writeProgressSnapshot({
      completed: this.completed,
      total: this.total,
      timestamp: Date.now(),
    })
  }

  private trackCachedAsset(url: string, total: number) {
    const key = this.normalizeAssetUrl(url)
    if (!key) {
      this.updateProgress(this.countCachedEntries(), total)
      return
    }

    const entry = this.assetsMap[key]
    if (entry) {
      entry.cached = true
    }
    if (!this.cachedAssets[key]) {
      this.cachedAssets[key] = true
      this.persistCachedAssets(total)
    }

    this.updateProgress(Math.min(this.countCachedEntries(), total), total)
  }

  private resetCachedAssets() {
    this.cachedAssets = {}
    this.persistCachedAssets(0)
  }

  private restoreCachedAssets() {
    const snapshot = readCachedAssetSnapshot()
    if (snapshot) {
      this.cachedAssets = { ...snapshot.entries }
      this.total = snapshot.total || 0
    }
  }

  private persistCachedAssets(total?: number) {
    writeCachedAssetSnapshot({
      entries: this.cachedAssets,
      total: typeof total === 'number' ? total : this.total,
      timestamp: Date.now(),
    })
  }

  private normalizeAssetUrl(url: string) {
    if (!url) {
      return ''
    }

    try {
      const parsed = new URL(url, window.location.origin)
      return parsed.origin + parsed.pathname + (parsed.search || '')
    } catch (error) {
      if (import.meta.env.VITE_NODE_ENV !== 'production') {
        console.warn('[PWA] Failed to normalize asset url', url, error)
      }
      return url
    }
  }

  private countCachedEntries() {
    return Object.values(this.cachedAssets).reduce((count, cached) => cached ? count + 1 : count, 0)
  }

  private pruneCachedAssets(validKeys: Set<string>) {
    for (const key of Object.keys(this.cachedAssets)) {
      if (!validKeys.has(key)) {
        delete this.cachedAssets[key]
      }
    }
  }
}

export const PWA = new PwaState()
